import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {PAGE_URLS} from '~/constants';
import {WEIGHT_LOSS_TWO_KEYS, WEIGHT_LOSS_TWO_QUESTIONNAIRES, YES_NO_VALUES} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';

interface Props {}

const MDQ2Questionnaire8Screen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_TWO_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_TWO_KEYS.DID_MEDICATION_WORK);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_TWO_KEYS.DID_MEDICATION_WORK]?.data,
  );

  const collectedData = mdq.collectedData;

  const rules = {
    weightDidLose: (attributes: any) => {
      if (isYesSelected) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            tooShort: '^This field is too short (minimum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
    sideEffects: (attributes: any) => {
      if (haveSideEffects) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            tooShort: '^This field is too short (minimum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
  };

  const {inputs, setInputs, trimSpaces, validation} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      weightDidLose: currentAnswer?.weightDidLose || '',
      haveSideEffects: currentAnswer?.haveSideEffects || '',
      sideEffects: currentAnswer?.sideEffects || '',
    },
    {rules},
  );

  const isYesSelected = inputs.selectedOption === YES_NO_VALUES.YES;
  const haveSideEffects = inputs.haveSideEffects === YES_NO_VALUES.YES;

  const onClickSubmit = () => {
    if (isYesSelected || haveSideEffects) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    const answerData = {
      key: WEIGHT_LOSS_TWO_KEYS.DID_MEDICATION_WORK,
      index: questionnaire?.index || '',
      data: {
        selectedOption: inputs?.selectedOption,
        weightDidLose: isYesSelected ? inputs?.weightDidLose : '',
        haveSideEffects: inputs?.haveSideEffects,
        sideEffects: haveSideEffects ? inputs?.sideEffects : '',
      },
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_TWO_KEYS.DID_MEDICATION_WORK]: answerData,
          },
        },
      }),
    );
    // dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.BMI_CALCULATOR}));
    history.push(PAGE_URLS.MDQ2_MEDICAL_HISTORY + window.location.search);
  };

  const renderWeightDidLose = () => {
    if (!isYesSelected) return null;
    return (
      <UiBox mt={12}>
        <UiText fontSize="md" fontWeight="700" mb={2.5}>
          {questionnaire?.questions?.sub}
        </UiText>
        <UiFormControl id="weightDidLose" isRequired isInvalid={!!validation?.errors?.weightDidLose}>
          <UiTextarea
            bg="white"
            value={inputs.weightDidLose}
            onChange={({target}) => setInputs({weightDidLose: target.value})}
            minH="100px"
            borderRadius={10}
          />
          {validation?.errors?.weightDidLose && (
            <UiFormErrorMessage>{validation?.errors?.weightDidLose[0]}</UiFormErrorMessage>
          )}
        </UiFormControl>
      </UiBox>
    );
  };

  const renderSideEffects = () => {
    if (!haveSideEffects) return null;
    return (
      <UiBox mt={8}>
        <UiText fontSize="md" fontWeight="700" mb={2.5}>
          {questionnaire?.questions?.sub3}
        </UiText>
        <UiFormControl id="sideEffects" isRequired isInvalid={!!validation?.errors?.sideEffects}>
          <UiTextarea
            bg="white"
            value={inputs.sideEffects}
            onChange={({target}) => setInputs({sideEffects: target.value})}
            minH="100px"
            borderRadius={10}
          />
          {validation?.errors?.sideEffects && (
            <UiFormErrorMessage>{validation?.errors?.sideEffects[0]}</UiFormErrorMessage>
          )}
        </UiFormControl>
      </UiBox>
    );
  };

  const renderQuestions = () => {
    if (!isYesSelected) return null;
    return (
      <UiBox w="100%" className="animate__animated animate__fadeInLeft">
        {renderWeightDidLose()}
      </UiBox>
    );
  };

  const renderSubQuestions = () => {
    if (!haveSideEffects) return null;
    return (
      <UiBox w="100%" className="animate__animated animate__fadeInLeft">
        {renderSideEffects()}
      </UiBox>
    );
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      actions={questionnaire?.actions}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      pageTitle={questionnaire?.title}
      disableNext={!inputs.selectedOption || !inputs.haveSideEffects}
    >
      <Col textAlign="center" mt={20} px={2}>
        <UiText fontWeight="bold" mb={6}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiSimpleGrid columns={2} spacing={4}>
          {questionnaire?.options?.map(value => (
            <UiButton
              key={`${value.label}-${value.value}`}
              title={value.label}
              variant={inputs.selectedOption === value.value ? 'primary' : 'outline'}
              onClick={() => setInputs({selectedOption: value.value})}
              whiteSpace="pre-wrap"
              color="trueBlack"
              borderColor="trueBlack"
              fontWeight={600}
              borderRadius={48}
              _hover={{}}
            />
          ))}
        </UiSimpleGrid>
        {renderQuestions()}
        <UiText fontWeight="700" my={6}>
          {questionnaire?.questions?.sub2}
        </UiText>
        <UiSimpleGrid columns={2} spacing={4}>
          {questionnaire?.subOptions?.map(value => (
            <UiButton
              key={`${value.label}-${value.value}`}
              title={value.label}
              variant={inputs.haveSideEffects === value.value ? 'primary' : 'outline'}
              onClick={() => setInputs({haveSideEffects: value.value})}
              whiteSpace="pre-wrap"
              color="trueBlack"
              borderColor="trueBlack"
              fontWeight={600}
              borderRadius={48}
              _hover={{}}
            />
          ))}
        </UiSimpleGrid>
        {renderSubQuestions()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default MDQ2Questionnaire8Screen;
